import React, { useState, useRef, useEffect } from 'react';
import { EmojiPicker } from 'frimousse';
import UiIcon from '../ui/UiIcon';

const EmojiGridBuilder = React.forwardRef(({ onCanvasChange }, ref) => {
  const [gridSize, setGridSize] = useState(2); // 2x2 default
  const [placements, setPlacements] = useState([]);
  const [selectedCell, setSelectedCell] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const canvasRef = useRef(null);

  // Expose methods to parent via ref
  React.useImperativeHandle(ref, () => ({
    getPlacements: () => placements,
    getGridSize: () => gridSize
  }));

  // Redraw canvas whenever placements or grid size changes
  useEffect(() => {
    drawCanvas();
  }, [placements, gridSize]);

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const canvasSize = 1024;
    canvas.width = canvasSize;
    canvas.height = canvasSize;

    // White background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvasSize, canvasSize);

    // Calculate cell size and padding
    const cellSize = canvasSize / gridSize;
    const padding = cellSize * 0.1; // 10% padding
    const fontSize = cellSize * 0.7; // 70% of cell size for emoji

    // Set font to Noto Emoji (matching CluePreview)
    ctx.font = `${fontSize}px "Noto Emoji", sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Draw each emoji
    placements.forEach(({ row, col, emoji }) => {
      const x = col * cellSize + cellSize / 2;
      const y = row * cellSize + cellSize / 2;
      ctx.fillStyle = 'black';
      ctx.fillText(emoji, x, y);
    });

    // Notify parent of canvas change
    if (onCanvasChange) {
      onCanvasChange(canvas.toDataURL('image/png'));
    }
  };

  const handleCellClick = (row, col) => {
    setSelectedCell({ row, col });
    setShowEmojiPicker(true);
  };

  const handleEmojiSelect = (emojiData) => {
    if (!selectedCell) return;

    const { row, col } = selectedCell;
    // Frimousse returns {emoji, label}
    const emoji = emojiData.emoji;

    // Update or add placement
    setPlacements(prev => {
      const existing = prev.findIndex(p => p.row === row && p.col === col);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { row, col, emoji };
        return updated;
      }
      return [...prev, { row, col, emoji }];
    });

    setShowEmojiPicker(false);
    setSelectedCell(null);
  };

  const clearCell = (row, col, e) => {
    e.stopPropagation();
    setPlacements(prev => prev.filter(p => !(p.row === row && p.col === col)));
  };

  const clearAll = () => {
    setPlacements([]);
  };

  const getEmojiForCell = (row, col) => {
    const placement = placements.find(p => p.row === row && p.col === col);
    return placement ? placement.emoji : null;
  };

  const getCanvasAsBase64 = () => {
    return canvasRef.current?.toDataURL('image/png') || null;
  };

  // Expose method to parent component
  useEffect(() => {
    if (window.emojiGridBuilderRef) {
      window.emojiGridBuilderRef.getCanvasAsBase64 = getCanvasAsBase64;
    }
  }, []);

  return (
    <div className="emoji-grid-builder">
      <div className="grid-size-selector">
        <label>Grid Size:</label>
        {[1, 2, 3, 4].map(size => (
          <label key={size} className="radio-option">
            <input
              type="radio"
              name="gridSize"
              value={size}
              checked={gridSize === size}
              onChange={(e) => setGridSize(parseInt(e.target.value))}
            />
            {size}x{size}
          </label>
        ))}
      </div>

      <div className="emoji-grid-preview">
        <div
          className="emoji-grid-interactive"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${gridSize}, 64px)`,
            gap: '6px',
            marginBottom: '12px'
          }}
        >
          {Array.from({ length: gridSize * gridSize }).map((_, index) => {
            const row = Math.floor(index / gridSize);
            const col = index % gridSize;
            const emoji = getEmojiForCell(row, col);

            return (
              <div
                key={index}
                className="emoji-grid-cell"
                onClick={() => handleCellClick(row, col)}
                style={{
                  position: 'relative',
                  aspectRatio: '1',
                  border: '2px dashed #ccc',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: emoji ? '36px' : '24px',
                  fontFamily: '"Noto Emoji", sans-serif',
                  background: emoji ? '#f5f5f5' : 'white',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#209bd4'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#ccc'}
              >
                {emoji || '+'}
                {emoji && (
                  <button
                    className="clear-cell-btn"
                    onClick={(e) => clearCell(row, col, e)}
                    style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px',
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      border: 'none',
                      background: '#dc3545',
                      color: 'white',
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0
                    }}
                  >
                    <UiIcon name="close" />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <button
          onClick={clearAll}
          className="btn-secondary"
          disabled={placements.length === 0}
        >
          Clear All
        </button>
      </div>

      {showEmojiPicker && (
        <div
          className="emoji-picker-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => {
            setShowEmojiPicker(false);
            setSelectedCell(null);
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '4px',
              padding: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              width: '840px',
              maxHeight: '800px'
            }}
          >
            <EmojiPicker.Root
              onEmojiSelect={handleEmojiSelect}
              style={{ '--frimousse-emoji-font': '"Noto Emoji", sans-serif' }}
              className="frimousse-custom"
            >
              <EmojiPicker.Search
                placeholder="Search emoji..."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginBottom: '12px',
                  outline: 'none'
                }}
              />
              <EmojiPicker.Viewport style={{ height: '400px', overflowY: 'auto' }}>
                <EmojiPicker.Loading>
                  <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                    Loading emojis...
                  </div>
                </EmojiPicker.Loading>
                <EmojiPicker.Empty>
                  <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                    No emoji found.
                  </div>
                </EmojiPicker.Empty>
                <EmojiPicker.List
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '12px',
                    padding: '12px'
                  }}
                />
              </EmojiPicker.Viewport>
            </EmojiPicker.Root>
            <style>{`
              .frimousse-custom button {
                background: none !important;
                border: none !important;
                padding: 12px !important;
                cursor: pointer !important;
                border-radius: 8px !important;
                transition: background 0.2s !important;
                aspect-ratio: 1 !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                min-height: 60px !important;
                font-size: 50px !important;
              }
              .frimousse-custom button:hover {
                background: #f0f0f0 !important;
              }
              .frimousse-custom button * {
                font-size: 50px !important;
                line-height: 1 !important;
              }
              .frimousse-custom [role="gridcell"] {
                font-size: 50px !important;
              }
            `}</style>
          </div>
        </div>
      )}

      {/* Hidden canvas for rendering */}
      <canvas
        ref={canvasRef}
        style={{ display: 'none' }}
      />
    </div>
  );
});

export default EmojiGridBuilder;
